import {
  getEegPlaybackManifest,
  getEegPlaybackTiles,
} from "../services/eegPlaybackApi";
import type { ComparisonRecording } from "../components/ComparisonView";

type LoadedChannelLike = {
  name?: string;
  data?: ArrayLike<number>;
  sampleRate?: number;
};

function decodeComparisonTile(
  buffer: ArrayBuffer,
): Promise<{
  channels: string[];
  columnArrays: Float32Array[];
  samplingRate: number;
  duration: number;
}> {
  if (typeof Worker === "undefined") {
    return Promise.reject(new Error("EEG tile worker unavailable"));
  }

  const worker = new Worker(
    new URL("../workers/eegTileWorker.ts", import.meta.url),
    { type: "module" },
  );

  return new Promise((resolve, reject) => {
    const reqId = `comparison-${Date.now()}`;
    const cleanup = () => {
      worker.terminate();
    };
    worker.onmessage = (event: MessageEvent<{ id?: string; ok?: boolean; data?: any; error?: string }>) => {
      const msg = event.data;
      if (msg?.id !== reqId) return;
      cleanup();
      if (!msg.ok) {
        reject(new Error(msg.error || "Worker decode failed"));
        return;
      }
      resolve({
        channels: msg.data?.channels || [],
        columnArrays: msg.data?.columnArrays || [],
        samplingRate: Number(msg.data?.samplingRate || 0),
        duration: Number(msg.data?.duration || 0),
      });
    };
    worker.onerror = (event) => {
      cleanup();
      reject(new Error(event.message || "EEG tile worker error"));
    };
    worker.postMessage(
      { id: reqId, type: "decode-arrow", payload: { buffer } },
      [buffer],
    );
  });
}

export function comparisonRecordingFromLoadedChannels(params: {
  id: string;
  name: string;
  channels: LoadedChannelLike[];
  samplingRate: number;
  duration: number;
}): ComparisonRecording {
  return {
    id: params.id,
    name: params.name,
    data: params.channels.map((channel) =>
      Array.from(channel.data ?? []),
    ),
    samplingRate: params.samplingRate,
    channelNames: params.channels.map(
      (channel, index) => channel.name || `Ch ${index + 1}`,
    ),
    duration: params.duration,
  };
}

export async function loadComparisonRecordingFromApi(params: {
  recordingId: string;
  name: string;
}): Promise<ComparisonRecording> {
  const manifestResponse = await getEegPlaybackManifest(params.recordingId);
  const manifest = manifestResponse.data?.data;
  const durationMs = Math.max(
    1,
    manifest?.logicalDurationMs || manifest?.durationMs || 10_000,
  );
  const windowMs = Math.min(durationMs, 10_000);
  const lodLevels = manifest?.lodLevels ?? [];
  const level = lodLevels.length > 0 ? lodLevels[lodLevels.length - 1].level : 2;

  const response = await getEegPlaybackTiles(
    params.recordingId,
    {
      startMs: 0,
      endMs: windowMs,
      level,
      speed: 1,
      transportMode: "inspect",
      requestOwner: "comparison",
    },
    {
      timeout: 30_000,
      headers: { Accept: "application/vnd.apache.arrow.stream" },
      responseType: "arraybuffer",
    },
  );

  const tile = await decodeComparisonTile(response.data as ArrayBuffer);
  return {
    id: params.recordingId,
    name: params.name,
    data: tile.columnArrays.map((column) => Array.from(column)),
    samplingRate: tile.samplingRate || manifest?.sampleRateHz || 256,
    channelNames:
      tile.channels.length > 0 ? tile.channels : manifest?.channels || [],
    duration: durationMs / 1000,
  };
}

export function studyRecordingOptionName(recording: {
  recording_id?: string;
  filename?: string;
  file_name?: string;
  recording_id_from_edf?: string;
}): string {
  return (
    recording.filename ||
    recording.file_name ||
    recording.recording_id_from_edf ||
    recording.recording_id ||
    "Recording"
  );
}
